import { createStore } from '../core/store';
import { NO_FORMAT, type FormatState } from '../editor/markdownCommands';
import { loadPrefs, type EditorPrefs } from '../platform/prefs';
import { loadPreference, resolveTheme, systemPrefersDark, type ResolvedTheme, type ThemePreference } from '../platform/theme';

/** Transient interface state. Never persisted except the theme and editor preferences. */

export interface DialogAction {
  id: string;
  label: string;
  tone?: 'primary' | 'danger';
}

export interface DialogRequest {
  title: string;
  message?: string;
  /** A short list stated plainly under the message (what exactly will happen). */
  details?: string[];
  /** One optional checkbox (unchecked by default). */
  option?: { label: string };
  actions: DialogAction[];
  /** Action chosen by Escape or clicking outside. */
  cancelId: string;
  /** Which action has the keyboard when the sheet opens (default: the primary one). */
  focusId?: string;
  resolve: (actionId: string, option?: boolean) => void;
}

export interface Notice {
  id: number;
  text: string;
  tone: 'info' | 'error';
}

/** What a right-click asked for, and where. */
export type ContextTarget = { kind: 'editor'; windowId: string } | { kind: 'tab'; docId: string } | { kind: 'canvas' };

export interface ContextMenuRequest {
  x: number;
  y: number;
  target: ContextTarget;
  /** Where focus returns when the menu closes. */
  returnFocus: HTMLElement | null;
}

export interface UiState {
  ready: boolean;
  /**
   * The opening, which plays on every page load (and on a deliberate replay):
   * `full` over an empty session, ending in the first centred window;
   * `restore` over restored work, which rises into place exactly as it was
   * left; `none` once it has finished or been interrupted.
   */
  intro: 'full' | 'restore' | 'none';
  prefs: EditorPrefs;
  palette: boolean;
  contextMenu: ContextMenuRequest | null;
  /** The viewport is too small for floating windows: the focused window fills it. */
  compact: boolean;
  theme: ThemePreference;
  resolvedTheme: ResolvedTheme;
  notice: Notice | null;
  dialog: DialogRequest | null;
  /** The person is typing: chrome recedes until the pointer moves or focus leaves. */
  typing: boolean;
  format: FormatState;
  /** Which control surface currently hosts the link field. */
  linkEditor: 'primary' | 'context' | null;
  /** Bumped on every selection change so floating controls can reposition. */
  selectionTick: number;
  selectionEmpty: boolean;
  /** True while a pointer drag is extending the selection. */
  dragging: boolean;
  /** The selection pill is on screen (Mod-K then edits the link there). */
  contextShown: boolean;
  /** Session storage is not durable in this browser. */
  ephemeral: boolean;
  /** Markdown documents currently shown rendered (read-only) instead of as source. */
  rendered: ReadonlySet<string>;
  /** The window layout surface, open for a window. */
  layoutFor: string | null;
  /** The account and cloud dialog is open. */
  account: boolean;
  /** On a compact canvas: the workspace overview is showing instead of the writing. */
  workspaces: boolean;
}

export const COMPACT_WIDTH = 700;
export const COMPACT_HEIGHT = 460;

export function isCompactCanvas(w: number, h: number): boolean {
  return w < COMPACT_WIDTH || h < COMPACT_HEIGHT;
}

const preference = loadPreference();

export const ui = createStore<UiState>({
  ready: false,
  intro: 'none',
  prefs: loadPrefs(),
  palette: false,
  contextMenu: null,
  compact: typeof window !== 'undefined' && isCompactCanvas(window.innerWidth, window.innerHeight),
  theme: preference,
  resolvedTheme: resolveTheme(preference, systemPrefersDark()),
  notice: null,
  dialog: null,
  typing: false,
  format: NO_FORMAT,
  linkEditor: null,
  selectionTick: 0,
  selectionEmpty: true,
  dragging: false,
  contextShown: false,
  ephemeral: false,
  rendered: new Set(),
  layoutFor: null,
  account: false,
  workspaces: false,
});

export function openAccount(): void {
  patchUi({ account: true, palette: false, contextMenu: null, workspaces: false });
}

export function patchUi(patch: Partial<UiState>): void {
  ui.set((s) => {
    for (const key in patch) {
      if (!Object.is(s[key as keyof UiState], patch[key as keyof UiState])) return { ...s, ...patch };
    }
    return s;
  });
}

let noticeId = 0;
let noticeTimer: ReturnType<typeof setTimeout> | undefined;

export function notify(text: string, tone: Notice['tone'] = 'info'): void {
  clearTimeout(noticeTimer);
  patchUi({ notice: { id: ++noticeId, text, tone } });
  noticeTimer = setTimeout(() => patchUi({ notice: null }), tone === 'error' ? 6000 : 3200);
}

export function dismissNotice(): void {
  clearTimeout(noticeTimer);
  patchUi({ notice: null });
}

export function ask(request: Omit<DialogRequest, 'resolve'>): Promise<string> {
  return askWithOption(request).then((r) => r.action);
}

/** Ask, also reporting the state of the request's checkbox. */
export function askWithOption(request: Omit<DialogRequest, 'resolve'>): Promise<{ action: string; option: boolean }> {
  return new Promise((resolve) => {
    patchUi({
      dialog: {
        ...request,
        resolve: (id, option) => {
          patchUi({ dialog: null });
          resolve({ action: id, option: !!option });
        },
      },
    });
  });
}

/** Show a Markdown document rendered, or as its source again. Never touches the text. */
export function setRendered(docId: string, on: boolean): void {
  const current = ui.get().rendered;
  if (current.has(docId) === on) return;
  const next = new Set(current);
  if (on) next.add(docId);
  else next.delete(docId);
  patchUi({ rendered: next });
}
